import { useEffect, useRef, useState } from "react";
import { Box } from "@smile2impress/components/relaunch";
import { useQueryString } from "hooks/useQueryString";
import { useCampaignContext } from "context/campaignContext";
import { PromoCampaign } from "providers/CampaignProvider";

import { isClientFromMedicare } from "components/PromoCampaigns/utils/isClientFromMedicare";
import Link from "components/Link";
import { getPromoContentComponent } from "components/PromoCampaigns/promoContent/getPromoContentComponent";

import { HeaderBannerProps } from "./HeaderBanner.types";

export const HeaderBanner = ({ isFixedPosition }: HeaderBannerProps) => {
    const { campaign } = useCampaignContext();
    const queryString = useQueryString();
    const bannerContainerRef = useRef<HTMLDivElement | null>(null);
    const [isFixed, setIsFixed] = useState(isFixedPosition);
    const bannerTopOffsetRef = useRef<number>(0);

    const [campaignData, setCampaignData] = useState<PromoCampaign | null>(
        campaign
    );

    useEffect(() => {
        setCampaignData(campaign);
    }, [campaign]);

    useEffect(() => {
        if (!isFixedPosition) return;

        const element = bannerContainerRef.current;
        if (!element) return;

        bannerTopOffsetRef.current =
            element.getBoundingClientRect().top + window.scrollY;
        const bannerTopOffset = 32;

        const handleScroll = () => {
            const scrollY = window.scrollY;
            setIsFixed(scrollY >= bannerTopOffsetRef.current - bannerTopOffset);
        };

        handleScroll();
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, [isFixedPosition]);

    if (!campaignData) return <></>;

    const { content, theme, campaignLink } = campaignData;

    const linkWithQueryString = queryString
        ? campaignLink + queryString
        : campaignLink;
    const fromMedicare = isClientFromMedicare();
    const isBannerVisible = campaignData && !fromMedicare;

    const contentComponentImport = getPromoContentComponent(
        content,
        "topBanner"
    );
    const ContentComponent = contentComponentImport?.() ?? null;

    return (
        <>
            {isBannerVisible && (
                <Box h={["32px"]} data-campaign-name={campaignData.name}>
                    <Link
                        {...(campaignLink && { href: linkWithQueryString })}
                        ref={bannerContainerRef}
                        overflow="hidden"
                        position={isFixed ? "fixed" : "absolute"}
                        top={0}
                        left={0}
                        w="100%"
                        zIndex="docked"
                    >
                        {ContentComponent && (
                            <ContentComponent
                                {...campaignData.content}
                                theme={theme}
                            />
                        )}
                    </Link>
                </Box>
            )}
        </>
    );
};
